/* eslint-disable @typescript-eslint/indent */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable no-restricted-syntax */
import CheckboxCustom from '@components/common/CheckboxCustom/CheckboxCustom';
import Image from 'next/image';
import closeIcon from '@assets/images/close-black.svg';
import closeIconLight from '@assets/images/close-light.svg';
import CollapsibleContainer from '@components/common/CollapsibleContainer/CollapsibleContainer';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import ButtonPrimary from '@components/common/ButtonPrimary';
import { store } from '@context/store';
import actionTypes from '@context/actionTypes';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import useIsMobile from '@hooks/useIsMobile';
// import { FILTER_CATEGORY_GUEST_RATING } from '@constants/filterValues';
import {
  AmenitiesFilter as AmenityFilterType,
  AppliedFilterList,
  RatingFilter,
  SrpFilterCount,
  SrpFilterLabel,
  SrpFilterOptions,
  SrpFilters,
  SrpFilters as SrpFiltersType,
  StarClassificationFilter,
} from '@customTypes/srp-filters';
import {
  filterAnyFilterClicked,
  filterAvailableHotelsClicked,
  filterClearAllClicked,
  filterShowResultsClicked,
  filterValueClicked,
} from '@events/filters';
import { Location } from '@components/Hotels/types';
import { useEvents } from '@events/EventsProvider';
import useAmplitudePayloads from '@hooks/useAmplitudePayloads';
import { APPLY_FILTER } from '@constants/amplitudeEvents';
import { getDateWithDashes } from '@helpers/date';
import { FILTER_CATEGORY_STAR_CLASSIFICATION } from '@constants/filterValues';
import styles from './SRPFilters.module.css';
import StarRatingFilter from './StarRatingFilter';
import AmenitiesFilter from './AmenitiesFilter';
import GuestRatingFilter from './GuestRatingFilter';
import VibesFilter from './VibesFilter';

type NameKeyType = StarClassificationFilter | AmenityFilterType | RatingFilter | string | number;

type Props = {
  srpFilters: SrpFiltersType | undefined;
  filterOptions: SrpFilterOptions | undefined;
  setSrpFilters: Dispatch<SetStateAction<SrpFiltersType | undefined>>;
  closeFilters: () => void;
  isFiltersExpanded: boolean;
  clearFilters: () => void;
  filterCount: SrpFilterCount | undefined;
  localShowOnlyAvailableHotels: boolean;
  setLocalShowOnlyAvailableHotels: Dispatch<SetStateAction<boolean>>;
  selectedLocation: Location | null;
  localClearAll: boolean;
  setLocalClearAll: Dispatch<SetStateAction<boolean>>;
  isRefreshData: boolean;
  setIsRefreshData: Dispatch<SetStateAction<boolean>>;
  handleChangeHotelsAvailable: (isAvailable?: boolean | null) => void;
  toggleAvailableHotelsCheckbox: (isToggled: boolean) => void;
};

export default function SRPFilters({
  srpFilters,
  filterOptions,
  setSrpFilters,
  closeFilters,
  isFiltersExpanded,
  clearFilters,
  filterCount,
  localShowOnlyAvailableHotels,
  setLocalShowOnlyAvailableHotels,
  selectedLocation,
  localClearAll,
  setLocalClearAll,
  isRefreshData,
  setIsRefreshData,
  handleChangeHotelsAvailable,
  toggleAvailableHotelsCheckbox,
}: Props) {
  const [isMidScreen, setIsMidScreen] = useState<boolean>(false);
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { searchDate } = state;
  const { track } = useEvents();
  const { generateFilterPayload, generateSearchLocationPayload } = useAmplitudePayloads();
  const { setItem, getItem, removeItem } = useSessionStorage();
  const isMobile = useIsMobile();

  // Vincent: The filtering feature probably needs to be refactored.
  // This "calculateFilterCount" function is doing too much.
  //
  // It's doing a lot state derivations manually
  // (filterCount, appliedFiltersList, categoryFilterParam...)
  // when they should all be derived using the built in hooks such as useMemo/useCallback
  //
  // It also seems to have a circular dependency on filterCount
  // (filterCount is a dependency but this function also sets the filterCount).
  // I can see the potential of an infinite render loop because of this circular dependency.

  const appliedSRPFilters = useMemo(() => {
    const filterList: AppliedFilterList[] = [];
    if (srpFilters && filterOptions) {
      for (const [key, value] of Object.entries(srpFilters)) {
        if (key !== 'url') {
          for (const [nestedKey, nestedValue] of Object.entries(value as Object)) {
            if (nestedKey && nestedValue && nestedKey !== '0') {
              // "final key value" can be a number (guest rating) or a boolean
              const finalKeyValue = !Number.isNaN(parseFloat(nestedKey))
                ? parseFloat(nestedKey)
                : nestedKey.toLocaleLowerCase();

              // Filters List is an object that contains the key/description/name of all filters
              filterList.push({
                label: filterOptions
                  ? filterOptions[key as SrpFilterLabel].find((option) => {
                      if (typeof option.name === 'string') {
                        return (option.name as string).toLowerCase() === finalKeyValue;
                      }
                      return option.name === finalKeyValue;
                    })?.description || ''
                  : '',
                category: key,
                name: nestedKey,
              });
            }
          }
        }
      }
    }
    return filterList;
  }, [filterOptions, srpFilters]);

  const updateTotalFilterCount = useCallback(() => {
    let value = 0;
    if (filterCount) {
      value = Object.values(filterCount).reduce((a, b) => a + b, 0);
    }
    if (localShowOnlyAvailableHotels) {
      value += 1;
    }
    dispatch({
      type: actionTypes.UPDATE_FILTER_COUNT,
      payload: value,
    });
  }, [dispatch, filterCount, localShowOnlyAvailableHotels]);

  const calculateFilterCount = useCallback(() => {
    if (!isMobile) {
      updateTotalFilterCount();
    } else if (isRefreshData) {
      updateTotalFilterCount();
      toggleAvailableHotelsCheckbox(localShowOnlyAvailableHotels);
      setIsRefreshData(false);
    }
  }, [
    isMobile,
    isRefreshData,
    updateTotalFilterCount,
    toggleAvailableHotelsCheckbox,
    localShowOnlyAvailableHotels,
    setIsRefreshData,
  ]);

  const trackFilterAppliedEvent = (appliedFilters?: SrpFilters) => {
    if (filterOptions && srpFilters) {
      const filterPropertyGroup = generateFilterPayload(
        appliedFilters || srpFilters,
        filterOptions,
        localShowOnlyAvailableHotels,
      );
      const searchLocationPropertyGroup = generateSearchLocationPayload(selectedLocation);

      track(APPLY_FILTER, {
        ...filterPropertyGroup,
        search_date: searchDate ? getDateWithDashes(searchDate) : null,
        ...searchLocationPropertyGroup,
      });
    }
  };

  const setFilters = (category: string, name: string, eventName: string) => {
    const newSrpFilters = srpFilters ? { ...srpFilters } : undefined;
    if (newSrpFilters) {
      if (
        category === FILTER_CATEGORY_STAR_CLASSIFICATION &&
        !newSrpFilters[category as keyof SrpFiltersType][name as keyof NameKeyType]
      ) {
        newSrpFilters[category]['any' as keyof StarClassificationFilter] = false;
        newSrpFilters[category]['=5' as keyof StarClassificationFilter] = false;
        newSrpFilters[category]['>=4' as keyof StarClassificationFilter] = false;
        newSrpFilters[category]['>=3' as keyof StarClassificationFilter] = false;
      }
      // TODO: fix this typescript hack
      const newValue: any =
        !newSrpFilters[category as keyof SrpFiltersType][name as keyof NameKeyType];
      newSrpFilters[category as keyof SrpFiltersType][name as keyof NameKeyType] =
        newValue as never;
      // GA4 event
      if (newValue && eventName) {
        filterValueClicked(eventName, selectedLocation, searchDate);
        filterAnyFilterClicked(selectedLocation, searchDate);
      }

      trackFilterAppliedEvent(newSrpFilters);

      dispatch({
        type: actionTypes.APPLY_SEARCH_FILTER,
        payload: newSrpFilters,
      });

      setSrpFilters(newSrpFilters);
      setLocalClearAll(false);
      if (!isMobile) {
        setItem(sessionStorageKeys.SRP_FILTERS, JSON.stringify(newSrpFilters));
      }
    }
  };

  const showFilteredResults = useCallback(() => {
    if (localClearAll) {
      updateTotalFilterCount();
      toggleAvailableHotelsCheckbox(false);
      removeItem(sessionStorageKeys.SRP_FILTERS);
      removeItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER);
      closeFilters();
      setLocalClearAll(false);
      return;
    }
    setItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER, localShowOnlyAvailableHotels.toString());
    setItem(sessionStorageKeys.SRP_FILTERS, JSON.stringify(srpFilters));
    toggleAvailableHotelsCheckbox(localShowOnlyAvailableHotels);
    updateTotalFilterCount();
    closeFilters();

    // GA4 Event
    filterShowResultsClicked(selectedLocation, searchDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localClearAll, localShowOnlyAvailableHotels, srpFilters]);

  const removeAllFilters = () => {
    clearFilters();
    if (isMobile) {
      setLocalShowOnlyAvailableHotels(false);
      setLocalClearAll(true);
    }
    // GA4 Event
    filterClearAllClicked(selectedLocation, searchDate);
  };

  useEffect(() => {
    if (localShowOnlyAvailableHotels) {
      // GA4 event
      filterAvailableHotelsClicked(selectedLocation, searchDate);
      filterAnyFilterClicked(selectedLocation, searchDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localShowOnlyAvailableHotels]);

  useEffect(() => {
    trackFilterAppliedEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localShowOnlyAvailableHotels]);

  useEffect(() => {
    if (isFiltersExpanded) {
      const storedFilters = getItem(sessionStorageKeys.SRP_FILTERS);
      const showOnlyAvailableFilter = getItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        if (parsedFilters.url === window.location.href) {
          setSrpFilters(parsedFilters);
          if (showOnlyAvailableFilter) {
            handleChangeHotelsAvailable(showOnlyAvailableFilter === 'true');
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFiltersExpanded]);

  useEffect(() => {
    if (srpFilters) {
      calculateFilterCount();
    }
  }, [srpFilters, localShowOnlyAvailableHotels, isRefreshData, calculateFilterCount]);

  useEffect(() => {
    if (window.innerWidth < 1440 && window.innerWidth > 1200) {
      setIsMidScreen(true);
    }
  }, []);

  return srpFilters ? (
    <div className="w-full relative max-h-full h-screen overflow-y-auto pb-28 d:h-full d:w-auto d:overflow-x-hidden d:pl-0 d:pr-2">
      <div className="flex h-60px justify-between items-center text-15 font-rp-pn-semi-bold w-full rounded-t-xl z-100 bg-white fixed border-b border-rp-gray-divider px-4 d:w-96 d:hidden">
        <button
          type="button"
          className="relative flex items-center opacity-60"
          onClick={() => closeFilters()}
        >
          <div className="w-3.5 h-3.5">
            <Image src={closeIcon} alt="close-filters" />
          </div>
        </button>
        <p className="text-center w-11/12 text-base text-rp-primary-black tracking-0.3">Filters</p>
        <button
          type="button"
          onClick={removeAllFilters}
          className="text-left text-rp-primary-dark font-rp-cera-medium tracking-tight cursor-pointer whitespace-nowrap mr-1 d:hidden"
        >
          Clear all
        </button>
      </div>
      <div className="pl-5 mt-20 pr-2.5 d:mt-7 d:pl-5 d:pr-0">
        {((appliedSRPFilters && appliedSRPFilters.length > 0) || localShowOnlyAvailableHotels) && (
          <div className="flex flex-wrap gap-3 border-b border-rp-gray-divider pb-5 d:hidden">
            {localShowOnlyAvailableHotels && (
              <div className="flex text-sm justify-end px-5 py-1.5 rounded-l-full rounded-r-full whitespace-nowrap bg-rp-light-background text-rp-primary-black">
                <div className="mr-3">Available</div>
                <div className="w-2.5 h-2.5 cursor-pointer">
                  <Image
                    src={closeIconLight}
                    alt="remove-item"
                    onClick={() => handleChangeHotelsAvailable()}
                  />
                </div>
              </div>
            )}
            {appliedSRPFilters &&
              appliedSRPFilters.length > 0 &&
              appliedSRPFilters.map((item, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${item.label}-${index}`}
                  className="flex text-sm justify-end pl-5 pr-3 py-1.5 rounded-l-full rounded-r-full whitespace-nowrap bg-rp-light-background text-rp-primary-black"
                >
                  <div className="mr-3">{item.label}</div>
                  <div className="w-4 h-4.5 cursor-pointer px-3px">
                    <Image
                      src={closeIconLight}
                      alt="remove-item"
                      onClick={() => setFilters(item.category, item.name, '')}
                    />
                  </div>
                </div>
              ))}
          </div>
        )}
        {searchDate && (
          <CollapsibleContainer
            title={`Availability ${localShowOnlyAvailableHotels && !isMobile ? '(1)' : ''}`}
            height="50px"
          >
            <div className="mt-0.5">
              <CheckboxCustom
                label="Only show hotels with availability"
                isChecked={localShowOnlyAvailableHotels}
                setIsChecked={handleChangeHotelsAvailable}
                id="availability-check-box"
                isDarkBorder
              />
            </div>
          </CollapsibleContainer>
        )}
        <StarRatingFilter
          srpFilters={srpFilters}
          setFilters={setFilters}
          filterOptions={filterOptions}
          isMobile={isMobile}
          isMidScreen={isMidScreen}
        />
        <AmenitiesFilter
          srpFilters={srpFilters}
          setFilters={setFilters}
          filterOptions={filterOptions}
          isMobile={isMobile}
          filterCount={filterCount}
        />

        <VibesFilter
          srpFilters={srpFilters}
          setFilters={setFilters}
          filterOptions={filterOptions}
          isMobile={isMobile}
          filterCount={filterCount}
        />

        <GuestRatingFilter
          srpFilters={srpFilters}
          setFilters={setFilters}
          filterOptions={filterOptions}
        />
        <button
          type="button"
          onClick={removeAllFilters}
          className="hidden text-left text-base text-rp-primary font-rp-cera-medium tracking-tight cursor-pointer mt-1 underline d:block"
        >
          Clear all
        </button>
      </div>
      <div
        className={`fixed bg-white w-full px-22px pb-6 pt-4 border-t border-rp-gray-divider d:hidden ${
          isFiltersExpanded ? styles.footerOpen : styles.footerClose
        }`}
      >
        {/* Book now button */}
        <div className="h-12 d:mb-2">
          <ButtonPrimary onClick={showFilteredResults}>Show Results</ButtonPrimary>
        </div>
      </div>
    </div>
  ) : null;
}
